import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../../pages/AboutPage.css";
import "./AboutPage.css";
import { CiEdit } from "react-icons/ci";

/* ─── API Config ─── */
// const BASE_URL = "https://staging.pteshstudios.com";
const BASE_URL = "https://api.jeyshawnng.com";
const API_BASE    = `${BASE_URL}/api/about`;
const UPLOAD_URL  = `${BASE_URL}/api/upload`;
const SERVER_ORIGIN = BASE_URL;

/* ─── Empty scaffold — modal fields when DB has no data ─── */
const EMPTY_DATA = {
  aboutUs: {
    heading:    "",
    paragraphs: [""],
    image:      "",
  },
  mission: {
    title: "",
    text:  "",
  },
  vision: {
    title: "",
    text:  "",
  },
  bannerImage: "",
};

/* ─── Display fallback — rendered on page when DB is empty ─── */
const DISPLAY_FALLBACK = {
  aboutUs: {
    heading: "Jeyshawn Engineering Limited is a leading indigenous Engineering, Procurement, Construction, Installation, and Commissioning (EPCIC) company dedicated to providing high-quality engineering and technical solutions to the oil & gas, energy, marine, manufacturing, and infrastructure sectors.",
    paragraphs: [
      "Established with a commitment to excellence and innovation, we have built a reputation for delivering complex projects with precision, efficiency, and professionalism. Our company was founded on the principle of creating dependable engineering solutions that support industrial development, operational growth, and sustainable progress across various sectors of the economy.",
      "With years of combined industry experience, our management team and technical professionals possess the expertise required to handle challenging projects in both onshore and offshore environments. We continuously invest in modern technologies, workforce development, and operational systems that enable us to deliver exceptional services that align with international best practices and client expectations.",
      "At Jeyshawn Engineering Limited, we understand that successful project delivery requires more than technical knowledge. It requires strategic planning, innovation, collaboration, effective communication, and an unwavering commitment to quality and safety. This understanding drives our approach to every project we undertake.",
      "Our integrated EPCIC solution covers the entire project lifecycle from initial planning, engineering studies, and procurement to construction, installation, commissioning, operations support, and maintenance services. By providing comprehensive solutions under one platform, we help our clients minimize operational risks, improve efficiency, reduce project timelines and maximize value.",
    ],
    image: "",
  },
  mission: {
    title: "OUR MISSION",
    text:  "At Jeyshawn, our mission is to execute projects safely, on time, and within budget, while exceeding client's expectations through engineering excellence, operational efficiency, and technological innovations.",
  },
  vision: {
    title: "OUR VISION",
    text:  "To be the most trusted partner in delivering world-class EPCIC solutions for sustainable and efficient energy infrastructure.",
  },
  bannerImage: "",
};

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${SERVER_ORIGIN}${url}`;
};

/* ═══════════════════════════════
   IMAGE UPLOAD FIELD
═══════════════════════════════ */
function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview, setPreview]     = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await axios.post(UPLOAD_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.success) throw new Error(data.message || "Upload failed");
      onUploaded(data.url);
      setPreview(resolveUrl(data.url));
    } catch (err) {
      setError("Upload failed. Try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && (
            <div className="editor-img-uploading">
              <div className="editor-loading__spinner editor-loading__spinner--sm" />
              <span>Uploading...</span>
            </div>
          )}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10 MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ═══════════════════════════════
   EDIT MODAL
   • dbData null  → all fields empty (EMPTY_DATA)
   • dbData set   → fields reflect DB values exactly
═══════════════════════════════ */
function EditModal({ dbData, onSave, onClose, saving }) {
  const buildDraft = () => {
    if (!dbData) return JSON.parse(JSON.stringify(EMPTY_DATA));

    const base = JSON.parse(JSON.stringify(EMPTY_DATA));

    base.bannerImage = dbData.bannerImage ?? "";

    if (dbData.aboutUs) {
      base.aboutUs.heading    = dbData.aboutUs.heading    ?? "";
      base.aboutUs.image      = dbData.aboutUs.image      ?? "";
      base.aboutUs.paragraphs = Array.isArray(dbData.aboutUs.paragraphs) && dbData.aboutUs.paragraphs.length > 0
        ? dbData.aboutUs.paragraphs
        : [""];
    }

    if (dbData.mission) {
      base.mission.title = dbData.mission.title ?? "";
      base.mission.text  = dbData.mission.text  ?? "";
    }

    if (dbData.vision) {
      base.vision.title = dbData.vision.title ?? "";
      base.vision.text  = dbData.vision.text  ?? "";
    }

    return base;
  };

  const [draft, setDraft] = useState(buildDraft);

  const setParagraph = (i, v) =>
    setDraft(d => {
      const paragraphs = [...d.aboutUs.paragraphs];
      paragraphs[i] = v;
      return { ...d, aboutUs: { ...d.aboutUs, paragraphs } };
    });

  const addParagraph    = () => setDraft(d => ({ ...d, aboutUs: { ...d.aboutUs, paragraphs: [...d.aboutUs.paragraphs, ""] } }));
  const removeParagraph = (i) => setDraft(d => {
    const paragraphs = d.aboutUs.paragraphs.filter((_, idx) => idx !== i);
    return { ...d, aboutUs: { ...d.aboutUs, paragraphs } };
  });

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit About Page Content</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* ── BANNER IMAGE ── */}
          <p className="editor-section__title">Banner Image</p>
          <div className="editor-group">
            <ImageUploadField
              label="Top Banner Image"
              currentUrl={draft.bannerImage}
              onUploaded={url => setDraft(d => ({ ...d, bannerImage: url }))}
            />
          </div>

          {/* ── ABOUT US ── */}
          <p className="editor-section__title">About Us Section</p>
          <div className="editor-group">
            <label>Opening Heading</label>
            <textarea
              rows={4}
              value={draft.aboutUs.heading}
              placeholder="Enter the opening heading for the About Us section"
              onChange={e => setDraft(d => ({ ...d, aboutUs: { ...d.aboutUs, heading: e.target.value } }))}
            />
            <ImageUploadField
              label="About Us Image"
              currentUrl={draft.aboutUs.image}
              onUploaded={url => setDraft(d => ({ ...d, aboutUs: { ...d.aboutUs, image: url } }))}
            />
          </div>
          <div className="editor-group">
            <p className="editor-group__label">Body Paragraphs</p>
            {draft.aboutUs.paragraphs.map((para, i) => (
              <div key={i} className="editor-value-row">
                <textarea
                  rows={4}
                  value={para}
                  placeholder={`Paragraph ${i + 1}`}
                  onChange={e => setParagraph(i, e.target.value)}
                  style={{ flex: 1, padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                />
                <button className="editor-value-row__remove" onClick={() => removeParagraph(i)} title="Remove">✕</button>
              </div>
            ))}
            <button className="editor-add-btn" onClick={addParagraph}>+ Add Paragraph</button>
          </div>

          {/* ── MISSION ── */}
          <p className="editor-section__title">Mission</p>
          <div className="editor-group">
            <label>Title</label>
            <input
              value={draft.mission.title}
              placeholder="e.g. OUR MISSION"
              onChange={e => setDraft(d => ({ ...d, mission: { ...d.mission, title: e.target.value } }))}
            />
            <label>Text</label>
            <textarea
              rows={4}
              value={draft.mission.text}
              placeholder="Describe your mission statement"
              onChange={e => setDraft(d => ({ ...d, mission: { ...d.mission, text: e.target.value } }))}
            />
          </div>

          {/* ── VISION ── */}
          <p className="editor-section__title">Vision</p>
          <div className="editor-group">
            <label>Title</label>
            <input
              value={draft.vision.title}
              placeholder="e.g. OUR VISION"
              onChange={e => setDraft(d => ({ ...d, vision: { ...d.vision, title: e.target.value } }))}
            />
            <label>Text</label>
            <textarea
              rows={3}
              value={draft.vision.text}
              placeholder="Describe your vision statement"
              onChange={e => setDraft(d => ({ ...d, vision: { ...d.vision, text: e.target.value } }))}
            />
          </div>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ═══════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════ */
function AboutUs({ data }) {
  const img = resolveUrl(data.aboutUs.image);
  return (
    <section className="about-us">
      <div className="about-us__header">
        <h2 className="section-title">About Us</h2>
        <span className="section-divider" />
      </div>
      <div className="about-us__body">
        <div className="about-us__img-wrap">
          <div className="about-us__img-accent" />
          {img && <img src={img} alt="Offshore oil platform with workers" className="about-us__img" />}
        </div>
        <div className="about-us__text">
          <div className="about_us_trans">
            <h2>{data.aboutUs.heading}</h2>
            {data.aboutUs.paragraphs.map((para, i) => <p key={i}>{para}</p>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function IMSPolicy({ data }) {
  return (
    <section className="ims-policy">
      <div className="ims-policy__inner">
        <h2 className="ims-policy__title">{data.mission.title}</h2>
        <p className="ims-policy__text">{data.mission.text}</p>
      </div>
      <div className="ims-policy__inner">
        <h2 className="ims-policy__title">{data.vision.title}</h2>
        <p className="ims-policy__text">{data.vision.text}</p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════
   MAIN EXPORT
═══════════════════════════════ */
export default function AboutPage() {
  // dbData  — raw API response (null = not yet fetched / DB empty)
  // pageData — what the page renders (dbData when available, DISPLAY_FALLBACK otherwise)
  const [dbData,    setDbData]    = useState(null);
  const [pageData,  setPageData]  = useState(DISPLAY_FALLBACK);
  const [loading,   setLoading]   = useState(true);
  const [fetchErr,  setFetchErr]  = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState(null);

  useEffect(() => { fetchPageData(); }, []);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      setFetchErr(null);
      const { data } = await axios.get(API_BASE);

      if (data.success && data.data && Object.keys(data.data).length > 0) {
        setDbData(data.data);
        setPageData(data.data);
      } else {
        // DB returned success but no content yet
        setDbData(null);
        setPageData(DISPLAY_FALLBACK);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setFetchErr("Could not load page data. Using defaults.");
      setDbData(null);
      setPageData(DISPLAY_FALLBACK);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (draft) => {
    try {
      setSaving(true);
      const { data } = await axios.put(API_BASE, draft);
      if (!data.success) throw new Error(data.message || "Save failed");
      await fetchPageData();
      setModalOpen(false);
      showToast("Changes saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg) => { setSaveMsg(msg); setTimeout(() => setSaveMsg(null), 3500); };

  const bannerImg = resolveUrl(pageData.bannerImage);

  return (
    <>
      {/* Edit FAB */}
                    <button
                      title="Edit page"
                      onClick={() => setModalOpen(true)}
                      style={{ position: "absolute", top: 70, right: 20, zIndex: 10, background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "50%", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                    >
                      <CiEdit size={24} color="#333" />
                    </button>

      {saveMsg && <div className="editor-toast">{saveMsg}</div>}
      {loading && <div className="editor-loading"><div className="editor-loading__spinner" /></div>}
      {fetchErr && (
        <div className="editor-error-banner">
          {fetchErr}
          <button onClick={fetchPageData}>Retry</button>
        </div>
      )}

      {/* Modal receives raw dbData — null means show empty fields */}
      {modalOpen && (
        <EditModal
          dbData={dbData}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
        />
      )}

      <div className="about_bg_color">
        <div className="img_carrier">
          {bannerImg && <img src={bannerImg} alt="" />}
        </div>
        <div className="about-page">
          <AboutUs data={pageData} />
          <IMSPolicy data={pageData} />
        </div>
      </div>
    </>
  );
}